import services from './module';
import {HttpSvcFactory} from './HttpSvc';
import {Utils} from '../underscore-extensions';
import {TranslateSvc} from './TranslateSvc';
import SpinnerSvc from './SpinnerSvc';

export const FOLDER_DATA_CHANGED = 'wave/videoFolder/changeData';
export const TREE_ITEM_SELECTED = 'wave/videoFolder/tree/itemSelected';
export const FOLDER_TREE_DATA_CHANGED = 'wave/videoFolder/tree/changeData';

export function isFolder(folder: VideoFolder.Folder | any): folder is VideoFolder.Folder {
    return folder.slug && folder.projects;
}

export class VideoFolderSvc {
    currentUser: anm.User = options.user;

    private baseRequestParams: VideoFolder.BaseRequestParams = {
        product: 'WAVE'
    };

    private httpCache = this.$cacheFactory('video-folder');
    newFolder = {} as VideoFolder.Folder;


    constructor(private $q: ng.IQService,
                private $cacheFactory: ng.ICacheFactoryService,
                private $rootScope: ng.IRootScopeService,
                private TranslateSvc: TranslateSvc,
                private HttpSvc: HttpSvcFactory) {
    }

    emitDataChanged = () => {
        this.$rootScope.$emit(FOLDER_DATA_CHANGED);
    }

    emitTreeItemSelected = (folderId: number) => {
        this.$rootScope.$emit(TREE_ITEM_SELECTED, folderId);
    }

    emitTreeDataChanged = () => {
        this.$rootScope.$emit(FOLDER_TREE_DATA_CHANGED);
    }

    isChild(parent: VideoFolder.Folder, child: VideoFolder.Folder): ng.IPromise<boolean> {
        const check = (f: VideoFolder.Folder): boolean => {
            if (!f.folder) {
                return false;
            } else if (f.folder.id === parent.id) {
                return true;
            } else {
                return check(f.folder);
            }
        };

        return this.getTreeById(child.id).then(check);
    }

    sortByCreatedDate(folders: VideoFolder.Folder[]) {
        return _.sortBy(folders, (folder) => -(new Date(folder.created).getTime()));
    }

    isNewFolder(folder: VideoFolder.Folder) {
        return folder.id === this.newFolder.id;
    }

    setNewCreatedFolder(newFolder: VideoFolder.Folder) {
        this.newFolder = newFolder;
    }

    clearNewCreatedFolder(folder: VideoFolder.Folder) {
        if (this.newFolder.id === folder.id) {
            this.newFolder = {} as VideoFolder.Folder;
        }
    }

    create(createParams: VideoFolder.CreateParams): ng.IPromise<VideoFolder.Folder> {
        const params = this.getParams(createParams);
        const url = `?${params}`;
        this.onFolderUpdate();

        return this.makeRequest('securePost', url)
            .then((folder) => {
                this.setNewCreatedFolder(folder);
                return folder;
            });
    }

    get(folderId: number) {
        const params = this.getParams({folderId});
        const url = `/fetch?${params}`;
        return this.makeRequest('secureGet', url, {cache: this.httpCache});
    }

    getByPath(path: string): ng.IPromise<VideoFolder.Folder> {
        const params = this.getParams({path: `/${path || ''}`});
        const url = `/fetch?${params}`;
        return this.makeRequest('secureGet', url, {cache: this.httpCache});
    }

    getRoot(username = this.currentUser.username) {
        const params = this.getParams();
        const url = `/users/${encodeURIComponent(username)}?${params}`;
        return this.makeRequest('secureGet', url, {cache: this.httpCache});
    }

    getFolderTree(username = this.currentUser.username): ng.IPromise<VideoFolder.Folder> {
        const params = this.getParams();
        const url = `/users/${encodeURIComponent(username)}/tree?${params}`;

        return this.makeRequest('secureGet', url, {cache: this.httpCache})
            .then((rootFolder: VideoFolder.Folder) => {
                rootFolder.name = this.TranslateSvc.get('wv.folder.breadcrumb.my-projects');
                return rootFolder;
            });
    }

    moveProject(moveParams: VideoFolder.MoveProjectParams) {
        this.onFolderUpdate();

        const params = this.getParams(moveParams);
        const url = `/project/move?${params}`;

        let request = this.makeRequest('securePut', url);
        SpinnerSvc.withSpinner(request);
        request.then(this.emitDataChanged);
        return request;
    }

    moveFolder(moveParams: VideoFolder.MoveParams) {
        const params = this.getParams(moveParams);
        const url = `/move?${params}`;
        this.onFolderUpdate();

        const request = this.makeRequest('securePut', url);
        SpinnerSvc.withSpinner(request);
        request.then(this.emitDataChanged);
        return request;
    }

    deleteFolder(deleteParams: VideoFolder.DeleteParams) {
        const params = this.getParams(deleteParams);
        const url = `/delete?${params}`;
        this.onFolderUpdate();

        return this.makeRequest('secureDelete', url).then(this.emitDataChanged);
    }

    renameFolder(renameFolderParams: VideoFolder.RenameParams) {
        const params = this.getParams(renameFolderParams);
        const url = `/rename?${params}`;
        this.onFolderUpdate();

        return this.makeRequest('securePut', url).then(this.emitDataChanged);
    }

    findInTree(folderId: number): ng.IPromise<VideoFolder.Folder> {
        const find = (parentFolder: VideoFolder.Folder) => {
            let result;
            if (parentFolder.id === folderId || !folderId) return parentFolder;

            parentFolder.folders.some(folder => {
                result = find(folder);
                return !!result;
            });

            return result;
        };

        return this.getFolderTree().then((parentFolder: VideoFolder.Folder) => find(parentFolder));
    }

    getTreeById(folderId: number): ng.IPromise<VideoFolder.Folder> {
        const find = (parentFolder: VideoFolder.Folder) => {
            let result;
            if (parentFolder.id === folderId || !folderId) return parentFolder;

            parentFolder.folders.forEach(folder => {
                const childFolder = find(folder);

                if (childFolder) {
                    result = {...parentFolder, folder: childFolder};
                }
            });

            return result;
        };

        return this.getFolderTree().then((parentFolder: VideoFolder.Folder) => find(parentFolder));
    }

    getTreeByPath(folderPath: string): ng.IPromise<VideoFolder.Folder> {
        if (!folderPath) return this.$q.resolve({});

        let slugs = folderPath.split('/');
        const find = (parentFolder: VideoFolder.Folder): undefined | VideoFolder.Folder => {
            const currentSlug = slugs[0];

            currentSlug && parentFolder.folders.forEach(folder => {
                if (folder.slug === currentSlug) {
                    slugs = slugs.slice(1);
                    find(folder);
                    parentFolder.folder = folder;
                }
            });

            return parentFolder;
        };

        return this.getFolderTree().then((parentFolder: VideoFolder.Folder) => find(parentFolder));
    }

    removeFolderFromTree(folderTree: VideoFolder.Folder, excludeFolder: VideoFolder.Folder) {
        if (folderTree.id === excludeFolder.parentId) {
            folderTree.folders = folderTree.folders.filter(folder => folder.id !== excludeFolder.id);
        } else {
            folderTree.folders = folderTree.folders.map(folder => this.removeFolderFromTree(folder, excludeFolder));
        }

        return {...folderTree};
    }

    onFolderUpdate() {
        this.httpCache.removeAll();
    }

    private makeRequest(method: anm.RequestMethods, url: string, config = {}): ng.IPromise<any> {
        let request = this.HttpSvc<VideoFolder.Folder>(`folder${url}`)[`${method}`](config)
            .then((r: ng.IHttpPromiseCallbackArg<any>) => r.data);
        return this.requestErrorHandler(request);
    }

    private requestErrorHandler(request: ng.IHttpPromise<any>) {
        return request.then((data: ApiError) => {
            const {errorCode} = data;
            return !errorCode || errorCode < 300
                ? this.$q.resolve(data)
                : this.$q.reject(data);
        });
    }

    private getParams(params = {}) {
        return Utils.toParams({...this.baseRequestParams, ...params});
    }
}

services.service('videoFolderSvc', VideoFolderSvc);



// WEBPACK FOOTER //
// ./app/assets/javascripts/services/videoFolderSvc.ts